import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { BeatLoader } from 'react-spinners'
import Sidebar from '../../layouts/Sidebar'
import Topbar from '../../layouts/Topbar'
import api from '../../config/api'

const emptyForm = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address_line1: '',
    city: '',
    country: '',
    company: {
        name: '',
        email: '',
        phone: '',
        address: '',
        logo_url: '',
        website: '',
    },
}

const getErrorMessage = (err, fallback) => {
    const data = err.response?.data
    if (!data) return fallback
    if (typeof data === 'string') return data
    if (data.detail) return data.detail
    if (data.message) return data.message
    return Object.entries(data)
        .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
        .join(' ')
}

const CreateClient = () => {
    const navigate = useNavigate()
    const [formData, setFormData] = useState(emptyForm)
    const [submitting, setSubmitting] = useState(false)
    const [formError, setFormError] = useState(null)
    const [successMessage, setSuccessMessage] = useState(null)

    const handleChange = (e) => {
        const { name, value } = e.target
        setFormData((prev) => ({ ...prev, [name]: value }))
    }

    const handleCompanyChange = (e) => {
        const { name, value } = e.target
        setFormData((prev) => ({
            ...prev,
            company: { ...prev.company, [name]: value },
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        setFormError(null)
        setSuccessMessage(null)

        try {
            await api.post('/clients/', formData)
            setSuccessMessage('Client created successfully.')
            setTimeout(() => navigate('/clients'), 1200)
        } catch (err) {
            console.error('Failed to create client', err)
            setFormError(getErrorMessage(err, 'Failed to create client. Please try again.'))
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content pb-0">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                        <div>
                            <h4 className="fw-bold mb-1">Create Client</h4>
                        </div>
                        <Link to="/clients" className="btn btn-light">
                            <i className="ti ti-arrow-left me-1" />
                            Back to Clients
                        </Link>
                    </div>

                    <form onSubmit={handleSubmit}>
                        {formError && (
                            <div className="alert alert-danger mb-4" role="alert">
                                <i className="ti ti-alert-circle me-2" />
                                {formError}
                            </div>
                        )}

                        {successMessage && (
                            <div className="alert alert-success mb-4" role="alert">
                                <i className="ti ti-check me-2" />
                                {successMessage}
                            </div>
                        )}

                        <div className="card shadow-sm mb-4">
                            <div className="card-header">
                                <h5 className="fw-bold mb-0">Client Information</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label htmlFor="first_name" className="form-label">
                                            First Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            id="first_name"
                                            type="text"
                                            name="first_name"
                                            className="form-control"
                                            placeholder="Jane"
                                            value={formData.first_name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="last_name" className="form-label">
                                            Last Name <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            id="last_name"
                                            type="text"
                                            name="last_name"
                                            className="form-control"
                                            placeholder="Smith"
                                            value={formData.last_name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="email" className="form-label">
                                            Email <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            id="email"
                                            type="email"
                                            name="email"
                                            className="form-control"
                                            placeholder="jane@example.com"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="phone" className="form-label">Phone</label>
                                        <input
                                            id="phone"
                                            type="text"
                                            name="phone"
                                            className="form-control"
                                            placeholder="+1 555-123-4567"
                                            value={formData.phone}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="address_line1" className="form-label">Address Line 1</label>
                                        <input
                                            id="address_line1"
                                            type="text"
                                            name="address_line1"
                                            className="form-control"
                                            placeholder="123 Main St"
                                            value={formData.address_line1}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="city" className="form-label">City</label>
                                        <input
                                            id="city"
                                            type="text"
                                            name="city"
                                            className="form-control"
                                            placeholder="Toronto"
                                            value={formData.city}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="country" className="form-label">Country</label>
                                        <input
                                            id="country"
                                            type="text"
                                            name="country"
                                            className="form-control"
                                            placeholder="Canada"
                                            value={formData.country}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card shadow-sm mb-4">
                            <div className="card-header">
                                <h5 className="fw-bold mb-0">Company Information</h5>
                            </div>
                            <div className="card-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label htmlFor="company-name" className="form-label">Company Name</label>
                                        <input
                                            id="company-name"
                                            type="text"
                                            name="name"
                                            className="form-control"
                                            placeholder="Acme Corp"
                                            value={formData.company.name}
                                            onChange={handleCompanyChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="company-email" className="form-label">Company Email</label>
                                        <input
                                            id="company-email"
                                            type="email"
                                            name="email"
                                            className="form-control"
                                            placeholder="billing@acme.com"
                                            value={formData.company.email}
                                            onChange={handleCompanyChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="company-phone" className="form-label">Company Phone</label>
                                        <input
                                            id="company-phone"
                                            type="text"
                                            name="phone"
                                            className="form-control"
                                            placeholder="+1 555-999-0000"
                                            value={formData.company.phone}
                                            onChange={handleCompanyChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="company-website" className="form-label">Website</label>
                                        <input
                                            id="company-website"
                                            type="url"
                                            name="website"
                                            className="form-control"
                                            placeholder="https://acme.com"
                                            value={formData.company.website}
                                            onChange={handleCompanyChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="company-address" className="form-label">Company Address</label>
                                        <input
                                            id="company-address"
                                            type="text"
                                            name="address"
                                            className="form-control"
                                            placeholder="456 Business Park, Toronto"
                                            value={formData.company.address}
                                            onChange={handleCompanyChange}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <label htmlFor="company-logo" className="form-label">Logo URL</label>
                                        <input
                                            id="company-logo"
                                            type="url"
                                            name="logo_url"
                                            className="form-control"
                                            placeholder="https://example.com/logo.png"
                                            value={formData.company.logo_url}
                                            onChange={handleCompanyChange}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-end gap-2 mb-4">
                            <Link to="/clients" className="btn btn-light" onClick={(e) => submitting && e.preventDefault()}>
                                Cancel
                            </Link>
                            <button type="submit" className="btn btn-primary" disabled={submitting}>
                                {submitting ? <BeatLoader color="#fff" size={8} /> : 'Create Client'}
                            </button>
                        </div>
                    </form>
                </div>

                <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
                    <p className="text-dark mb-0">
                        {new Date().getFullYear()} © <span className="link-primary">Burray Invoice</span>, All Rights Reserved
                    </p>
                </div>
            </div>
        </div>
    )
}

export default CreateClient
